// ─────────────────────────────────────────────────────────────────────────────
// THE INDIVIDUAL LICENCE AND THE FIRM'S, AS TWO FIELDS THAT SAY WHICH IS WHICH.
//
// 🚨 THE PROFILE TAB HAD ONE FIELD CALLED "License #" and the Overview tab had
// one row called "NMLS ID", and neither said whose. For a loan officer those are
// two different registrations: the originator holds an individual NMLS ID and
// the company holds its own. An account holder looking at one number cannot tell
// which was recorded, and a rep correcting it cannot tell which they are editing.
//
// ⚠️ FOR A REALTOR IT IS NOT SIMPLY "THE SAME NUMBER" EITHER. A broker trading
// as themselves uses their own licence, but a registered brokerage entity has
// its own — TREC issues a Broker Company licence (the "-BB" suffix on the
// public roll) and Florida's DBPR issues a corporation licence. So the firm's
// number is a separate field for realtors too, left empty rather than mirrored
// from the individual: auto-filling it would assert a registration nobody has
// checked, and that is exactly the error two realtors already made by typing
// their brokerage's number into their own licence field.
//
// 🔑 READ FROM THE LICENCE-VERIFICATION ENDPOINT, which already knows the
// registry, both numbers and the account. The alternative was widening the
// partners list SELECT with company_nmls — a new column in a hot query, which
// is the migration hazard that file keeps its late reads out of.
//
// ⚠️ TWO SAVE TARGETS, SO TWO BUTTONS. The individual number belongs to the
// partner and the firm's belongs to the account; one button writing to both
// would leave a partial failure with nothing saying which half landed.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const LABEL = {
  input: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 },
  field: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 13, boxSizing: 'border-box', outline: 'none' },
  note: { fontSize: 11.5, color: '#6b7280', marginTop: 4, lineHeight: 1.5 },
};

export default function PartnerLicenseFields({ partnerId, token, isAccountHolder, onSaved }) {
  const [data, setData] = useState(null);
  const [companyValue, setCompanyValue] = useState('');
  // 🚨 THE INDIVIDUAL NUMBER IS EDITED HERE NOW. It used to be read-only with a
  // note pointing at a generic "License #" field above — and that field wrote
  // partner_profiles.license_number, the legacy mirror, which NOTHING that
  // matters reads. So a correction typed there appeared to save and changed
  // nothing: Chris Brosig's individual field still held his firm's NMLS ID
  // after somebody had already corrected it once.
  const [individualValue, setIndividualValue] = useState('');
  const [msg, setMsg] = useState(null);
  const [indMsg, setIndMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [indBusy, setIndBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    if (!partnerId || !token) return;
    fetch(`${API}/api/admin/partners/${partnerId}/license-verification`, { headers })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) return;
        setData(d);
        const co = (d.subjects || []).find((s) => s.subject === 'account');
        const own = (d.subjects || []).find((s) => s.subject === 'partner');
        // 🔑 SEEDED ONCE. Re-seeding on every reload would discard an edit in
        // progress the moment anything else refreshed.
        setCompanyValue((v) => (v === '' && co && co.number ? co.number : v));
        setIndividualValue((v) => (v === '' && own && own.number ? own.number : v));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, token]);

  useEffect(load, [load]);
  if (!data || !data.registry) return null;

  const isNmls = data.registry === 'nmls';
  const individual = (data.subjects || []).find((s) => s.subject === 'partner');
  const company = (data.subjects || []).find((s) => s.subject === 'account');

  // ── 🚨 THE SOURCE OF TRUTH, NOT THE MIRROR ──────────────────────────────
  //
  // PUT /api/admin/partners/:id now dispatches `license` on the registry:
  // nmls_id for an originator, real_estate_license_number for a realtor, and
  // partner_profiles.license_number kept in step by the same write because the
  // partners list and the Overview row read it. One request, one writer, so an
  // edit here shows up on every surface rather than on one of them.
  //
  // ⚠️ AND THE SERVER CLEARS THE VERIFICATION. utils/licenceIdentityChanged
  // watches both of those columns: changing the number invalidates a manual
  // reading and re-runs an automated one, because a verification is of a NUMBER
  // and that number is no longer the one somebody looked up.
  const saveIndividual = async () => {
    setIndBusy(true); setIndMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/partners/${partnerId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ license: individualValue, source: 'doorlypro' }),
      });
      const d = await res.json();
      // 🔑 THE SERVER'S SENTENCE. "That is the firm's NMLS ID, not this person's"
      // is the whole value of the refusal, and a generic failure message would
      // throw it away.
      if (!res.ok) { setIndMsg(d.error || 'Could not save the number.'); return; }
      setIndMsg(d.licence_recheck && d.licence_recheck.acted
        ? `✓ Saved — ${d.licence_recheck.action}`
        : '✓ Saved');
      load();
      if (onSaved) onSaved(individualValue);
      setTimeout(() => setIndMsg(null), 4000);
    } catch (_) {
      setIndMsg('Could not reach the server. Nothing was saved.');
    } finally { setIndBusy(false); }
  };

  const saveCompany = async () => {
    setBusy(true); setMsg(null);
    try {
      // ⚠️ THE ACCOUNT ENDPOINT, NOT THE PARTNER ONE. This number belongs to the
      // firm, and editing it clears the firm's verification server-side —
      // a staff member verified a NUMBER, and once it changes "verified" would
      // be a claim about a value nobody looked up.
      const res = await fetch(`${API}/api/admin/partners/accounts/${data.partner.account_id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(isNmls
          ? { company_nmls: companyValue }
          : { entity_license_number: companyValue }),
      });
      const d = await res.json();
      if (!res.ok) { setMsg(d.error || 'Could not save the firm number.'); return; }
      setMsg('✓ Firm number saved');
      load();
      setTimeout(() => setMsg(null), 3000);
    } catch (_) {
      setMsg('Could not reach the server. Nothing was saved.');
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* 🚨 ONE FIELD FOR THIS NUMBER, AND IT IS THIS ONE. The Profile tab also
          carried a generic "License #" input bound to the same value; two inputs
          for one number is how a form starts disagreeing with itself, and in
          this case they were not even bound to the same COLUMN — that one wrote
          the legacy mirror and this one reads the column every gate consults. */}
      <div>
        <label style={LABEL.input}>
          {isNmls ? 'Individual NMLS ID' : 'Individual real estate license #'}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={individualValue}
            onChange={(e) => setIndividualValue(e.target.value)}
            placeholder="not on record"
            style={LABEL.field} />
          <button type="button" onClick={saveIndividual}
            disabled={indBusy || individualValue === (individual?.number || '')}
            style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 8, border: 'none',
              background: indBusy || individualValue === (individual?.number || '')
                ? '#e5e7eb' : '#3a6ad9',
              color: indBusy || individualValue === (individual?.number || '')
                ? '#9ca3af' : '#fff',
              fontWeight: 700, fontSize: 12.5,
              cursor: individualValue === (individual?.number || '')
                ? 'not-allowed' : 'pointer' }}>
            {indBusy ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div style={LABEL.note}>
          {individual?.label ? `${individual.label} · ` : ''}
          {isNmls
            ? 'This person’s own NMLS registration — never the firm’s. NMLS registers a company '
              + 'and an individual separately, so the two numbers are never the same.'
            : 'This person’s own state real estate license.'}
          {' '}Saving it clears any verification on file, because a check is of a number.
        </div>
        {indMsg && (
          <div style={{ ...LABEL.note, color: indMsg.startsWith('✓') ? '#166534' : '#b91c1c',
            fontWeight: 600 }}>{indMsg}</div>
        )}
      </div>

      {/* ⚠️ THE FIRM'S NUMBER IS SHOWN ONLY TO AN ACCOUNT HOLDER'S RECORD. An
          ordinary member does not control it and showing it on their profile
          invites an edit that belongs to somebody else. */}
      {isAccountHolder && data.partner.account_id && (
        <div>
          <label style={LABEL.input}>
            {isNmls ? 'Company NMLS ID' : 'Company / brokerage license #'}
            {data.partner.company_name ? ` — ${data.partner.company_name}` : ''}
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={companyValue}
              onChange={(e) => setCompanyValue(e.target.value)}
              placeholder="not on record"
              style={LABEL.field} />
            <button type="button" onClick={saveCompany}
              disabled={busy || companyValue === (company?.number || '')}
              style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 8, border: 'none',
                background: busy || companyValue === (company?.number || '') ? '#e5e7eb' : '#3a6ad9',
                color: busy || companyValue === (company?.number || '') ? '#9ca3af' : '#fff',
                fontWeight: 700, fontSize: 12.5,
                cursor: companyValue === (company?.number || '') ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div style={LABEL.note}>
            {company?.label ? `${company.label} · ` : ''}
            {isNmls
              ? 'The firm’s own NMLS registration — a different number from the originator’s, '
                + 'and the one the borrower authorization prints.'
              : 'A brokerage trading as a registered entity has its own licence (TREC issues a '
                + 'Broker Company number); a broker trading as themselves may not. Left empty '
                + 'rather than copied from the individual number.'}
            {' '}Saving it clears the firm’s verification, because a check is of a number.
          </div>
          {msg && (
            <div style={{ ...LABEL.note, color: msg.startsWith('✓') ? '#166534' : '#b91c1c',
              fontWeight: 600 }}>{msg}</div>
          )}
        </div>
      )}
    </>
  );
}
